import { useState } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Plus, Minus } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';

type Batch = {
  id: string;
  name: string;
}

type BatchAssignment = {
  id?: string;
  batchId: string;
  isCompleted: boolean;
}

interface BatchesListProps {
  batches: any[];
  availableBatches: any[];
  onBatchAssignmentsChange: (batches: any[]) => void;
}

const BatchesList = ({ batches, availableBatches, onBatchAssignmentsChange }: any) => {
  const addBatchAssignment = () => {
    // Find which batches are not yet assigned
    const assignedBatchIds = batches?.map(ba => ba.batchId);
    const availableBatchIds = availableBatches?.map(b => b.id);
    const unassignedBatchIds = availableBatchIds?.filter(id => !assignedBatchIds.includes(id));

    // If there are no unassigned batches, return
    if (unassignedBatchIds?.length === 0) return;

    const newBatchAssignment: BatchAssignment = {
      id: Date.now().toString(),
      batchId: '',
      isCompleted: false
    };

    onBatchAssignmentsChange([...batches, newBatchAssignment]);
  };

  const removeBatchAssignment = (id: string) => {
    onBatchAssignmentsChange(batches.filter(ba => ba.batchId !== id));
  };

  const updateBatchAssignment = (id: string, updates: Partial<BatchAssignment>) => {
    onBatchAssignmentsChange(
      batches?.map(ba =>
        ba.batchId === id ? { ...ba, ...updates } : ba
      )
    );
  };

  // Get batches that are not yet assigned
  const getUnassignedBatches = (currentBatchId?: string) => {
    const assignedBatchIds = batches?.map(ba => ba.batchId);
    return availableBatches?.filter(batch =>
      !assignedBatchIds.includes(batch.id) || batch.id === currentBatchId
    );
  };

  return (
    <div className="space-y-4 border rounded-lg p-4 max-h-[80vh] overflow-y-auto">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-bytesplash-blue">Batches</h3>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addBatchAssignment}
          disabled={batches?.length >= availableBatches?.length}
        >
          <Plus className="mr-1 h-4 w-4" />
          Add Batch
        </Button>
      </div>

      {batches?.map((batchAssignment, index) => {
        const batch = availableBatches?.find(b => b.id === batchAssignment.batchId);

        return (
          <>
            <div key={batchAssignment?.batchId} className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center border-b pb-4 last:border-0 ">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-bytesplash-blue font-medium">
                    Batches {index + 1}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                    onClick={() => removeBatchAssignment(batchAssignment.batchId)}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id={`completed-${batchAssignment.batchId}`}
                    checked={batchAssignment.isCompleted}
                    onCheckedChange={(checked) => {
                      updateBatchAssignment(batchAssignment.batchId, { isCompleted: checked })
                    }
                    }
                  />
                  <Label htmlFor={`completed-${batchAssignment.batchId}`}>
                    Is Module Completed
                  </Label>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor={`batch-${batchAssignment.batchId}`}>
                  Select Batch<span className="text-red-500">*</span>
                </Label>
                <Select
                  value={batchAssignment.batchId}
                  onValueChange={(value) =>
                    updateBatchAssignment(batchAssignment.batchId, { batchId: value })
                  }
                >
                  <SelectTrigger id={`batch-${batchAssignment.batchId}`}>
                    <SelectValue placeholder="Select a batch" />
                  </SelectTrigger>
                  <SelectContent>
                    {getUnassignedBatches(batchAssignment.batchId)?.map(batch => (
                      <SelectItem key={batch.id} value={batch.id}>
                        {batch.batchName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

            </div>
            {
              index == batches?.length - 1 &&
              <div className="pt-4">

                <Button
                  type="button"
                  size="sm"
                  onClick={addBatchAssignment}
                  disabled={batches?.length >= availableBatches?.length}
                >
                  <Plus className="mr-1 h-4 w-4" />
                  Add Recording
                </Button>
              </div>

            }
          </>
        );
      })}

      {batches?.length === 0 && (
        <div className="text-center py-8 border rounded-md bg-gray-50">
          <p className="text-gray-500 mb-2">No batches assigned yet.</p>
          <Button
            type="button"
            variant="outline"
            onClick={addBatchAssignment}
          >
            <Plus className="mr-1 h-4 w-4" />
            Add First Batch
          </Button>
        </div>
      )}
    </div>
  );
};

export default BatchesList;